import { reactive } from 'vue'
import compareAsc from 'date-fns/compareAsc'
import parseISO from 'date-fns/parseISO'
import formatISO from 'date-fns/formatISO'
import isSameDay from 'date-fns/isSameDay'
import isSunday from 'date-fns/isSunday'

export default {
  debug: false,

  state: reactive({
    /**
     * Array that holds all orders with product quantities
     *
     * Example of an stock order object:
     * [{ orderDate: Date, orderId: Int, submitted: Bool, editable: Bool, hasChanges: Bool, products: [{item: Object, originalQuantity: int, newQuantity: int}] }]
     */
    stock: [],
    weekDates: [],
    excludedDates: [],
  }),

  getStock() {
    return this.state.stock
  },

  /**
   * Update product quantities for an order in local stock
   *
   * Please be aware, that updateStock should only receive data, if an order of the product can be made on the specified day (e.g. holidays).
   * It will not check, if the product order is valid, though the backend will stop with an error in this case.
   */
  updateStock({orderDate, product, quantity}) {
    let item = this.state.stock.find(item => compareAsc(item.orderDate, orderDate) === 0)

    if (item !== undefined) {
      let itemIndex = this.state.stock.indexOf(item)
      let productToUpdate = item.products.find(itemProduct => itemProduct?.item?.id === product.id)

      if (productToUpdate !== undefined) {
        let productIndex = item.products.indexOf(productToUpdate)

        if (productToUpdate.originalQuantity === 0 && quantity === 0) {
          item.products.splice(productIndex, 1)

          item.hasChanges = item.products.length === 0 ? false : item.hasChanges
        } else {
          productToUpdate.newQuantity = quantity
          item.hasChanges = true
          item.products.splice(productIndex, 1, productToUpdate)
        }

        this.state.stock.splice(itemIndex, 1, item)
      } else if (productToUpdate === undefined && quantity > 0) {
        item.products.push({item: product, originalQuantity: 0, newQuantity: quantity})
        item.hasChanges = true

        this.state.stock.splice(itemIndex, 1, item)
      }
    }
  },

  /**
   * Add orders for current week to stock
   *
   * Everytime the query is refetched, this should be called.
   * The orders will be added to the stock, as the name implies.
   * So existing orders in the stock will not be deleted.
   *
   * It only adds orders to stock, if the order does NOT exist in stock
   * So placed orders of users are not overwritten
   */
  addOrdersToStock(orders) {
    for (const date of this.state.weekDates) {
      const order = orders.find((order) => compareAsc(parseISO(order.date), date) === 0)
      const stockItem = this.state.stock.find(item => compareAsc(item.orderDate, date) === 0)

      if (stockItem === undefined) {
        const products = order?.products?.map(product => ({'item': product, 'originalQuantity': product.pivot.quantity, 'newQuantity': product.pivot.quantity })) ?? []

        this.state.stock.push({
          'orderDate': date,
          'orderId': order?.id ?? null,
          'submitted': order?.submitted ?? false,
          'editable': order?.editable ?? true,
          'hasChanges': false,
          'products': products
        })
      }
    }
  },

  /**
   * Returns an array of UpsertOrderInput objects (see mutation declaration)
   *
   * All orders before the next possibleOrderDate will be removed,
   * as all orders outside of this week. Also orders without changes
   * will be removed.
   *
   * For holidays: This function expects that on not orderable dates where
   * are no changes (see updateStock doc).
   */
  mapStockForUpsertMutation(nextPossibleOrderDate) {
    let result = []

    if (typeof nextPossibleOrderDate !== Date) {
      nextPossibleOrderDate = parseISO(nextPossibleOrderDate)
    }

    for (let item of this.state.stock) {
      if (
        item.hasChanges
        && this.isOrderable(item.orderDate, nextPossibleOrderDate)
        && this.isInWeekDates(item.orderDate)
      ) {
        result.push({
          id: item.orderId,
          orderDate: formatISO(item.orderDate, {representation: "date"}),
          products: {
            sync: item.products.map(product => ({
              id: product.item.id,
              quantity: product.newQuantity
            }))
          },
          action: item.submitted ? 'SUBMIT_UPDATE' : 'SUBMIT'
        })
      }
    }

    return result
  },

  getValidWeekOrders(nextPossibleOrderDate) {
    let result = []

    if (typeof nextPossibleOrderDate !== Date) {
      nextPossibleOrderDate = parseISO(nextPossibleOrderDate)
    }

    for (let item of this.state.stock) {
      if (
        // item.hasChanges
        this.isOrderable(item.orderDate, nextPossibleOrderDate)
        && this.isInWeekDates(item.orderDate)
      ) {
        item = Object.assign({}, item)
        item.groupedProducts = this.groupByCategory(item.products)
        delete item.products
        result.push(item)
      }
    }

    return result
  },

  isInWeekDates(orderDate) {
    return this.state.weekDates.find(date => compareAsc(orderDate, date) === 0) !== undefined
  },

  isOrderable(orderDate, nextPossibleOrderDate) {
    if (isSunday(orderDate)) {
      return false
    }

    return compareAsc(orderDate, nextPossibleOrderDate) >= 0
  },

  isExcluded(date) {
    if (this.state.excludedDates === []) {
      return false
    }

    return this.state.excludedDates.find(day => isSameDay(date, day)) !== undefined
  },

  setWeekDates(weekDates) {
    this.state.weekDates = weekDates
  },

  setExcludedDates(excludedDates) {
    this.state.excludedDates = []

    for (let date of excludedDates) {
      this.state.excludedDates.push(parseISO(date))
    }
  },

  groupByCategory(products) {
    let result = {}

    for (const product of products) {
      const categoryName = product.item.primary_category?.name ?? 'Unkategorisiert'

      if (!result.hasOwnProperty(categoryName)) {
        result[categoryName] = []
      }

      result[categoryName].push(product)
    }

    return result
  }
}
